/**
 * Owner: Masa Bayu
 * File: CommunityPostDetail.tsx
 * Date:Time: 2026-10-17 04:22
 * Description: Dedicated community post detail — post + comment thread + reply compose,
 *   wired to the real QXCircle client API.
 */

import { useCallback, useEffect, useState } from 'react';
import { useAccountProfile } from '../auth/use-account-profile';
import {
  createQxCircleComment,
  getQxCircleComments,
  getQxCirclePost,
  type QxCircleComment,
  type QxCircleFeedPost,
} from '../client/qxcircle-api';
import { CommunityPostCard } from './CommunityPostCard';

export interface CommunityPostDetailProps {
  readonly postId: string;
  readonly onNavigate: (path: string) => void;
}

export function CommunityPostDetail({ postId, onNavigate }: CommunityPostDetailProps) {
  const account = useAccountProfile();
  const [post, setPost] = useState<QxCircleFeedPost | null>(null);
  const [comments, setComments] = useState<readonly QxCircleComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    Promise.all([getQxCirclePost(postId), getQxCircleComments(postId)])
      .then(([detail, thread]) => {
        if (!alive) return;
        setPost(detail.post);
        setComments(thread.items);
      })
      .catch(() => {
        if (alive) setPost(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [postId]);

  useEffect(() => load(), [load]);

  const submit = async () => {
    const body = draft.trim();
    if (body.length === 0 || sending) return;
    setSending(true);
    try {
      await createQxCircleComment(postId, body);
      setDraft('');
      load();
    } catch {
      /* keep draft on failure */
    } finally {
      setSending(false);
    }
  };

  if (loading) return <p className="ddx__status">Loading post…</p>;

  if (post === null) {
    return (
      <div className="ddx__empty">
        <h3>Post not found</h3>
        <p>It may have been removed by its author.</p>
        <button
          type="button"
          className="ddx__cta ddx__cta--solid"
          onClick={() => {
            onNavigate('/circle');
          }}
        >
          Back to feed
        </button>
      </div>
    );
  }

  return (
    <div className="cmc">
      <header>
        <p className="ddx__eyebrow">Community · Post</p>
        <h2 className="ddx__title">Discussion</h2>
      </header>

      <CommunityPostCard post={post} onNavigate={onNavigate} onChanged={load} />

      <section aria-label="Comments" className="cmc__comments">
        <h3 className="ddx__head">Comments ({String(comments.length)})</h3>
        {comments.length === 0 ? (
          <p className="ddx__status">No comments yet — start the conversation.</p>
        ) : (
          comments.map((comment) => (
            <div className="cmc__comment" key={comment.id}>
              <div className="cmc__comment-head">
                <span className="cmc__name">{comment.authorName}</span>
                <span className="cmc__handle">@{comment.authorHandle}</span>
              </div>
              <p className="cmc__body">{comment.body}</p>
            </div>
          ))
        )}
      </section>

      {account.signedIn ? (
        <div className="cmc__gate">
          <h3>Reply as {account.displayName ?? 'Member'}</h3>
          <div className="cmc__compose">
            <textarea
              value={draft}
              placeholder="Add a comment…"
              onChange={(event) => {
                setDraft(event.target.value);
              }}
            />
            <div className="ddx__actions">
              <button
                type="button"
                className="ddx__cta ddx__cta--solid"
                disabled={sending || draft.trim().length === 0}
                onClick={() => {
                  void submit();
                }}
              >
                Comment
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="cmc__gate">
          <h3>Sign in to comment</h3>
          <p>Guests can read the discussion. Sign in to join it.</p>
          <div className="ddx__actions">
            <button
              type="button"
              className="ddx__cta ddx__cta--solid"
              onClick={() => {
                onNavigate('/account');
              }}
            >
              Sign in
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
